import { LogInPaths } from '@bbkAdminComponents/log-in-flow/log-in-paths';

/**
 * Auth-landing pages are never valid deep-link targets. Accepting one lets a
 * self-referential `redirectUri` (e.g. `/company/sign-in`) bounce the user
 * through an extra sign-in reload before the selector, and semantically a deep
 * link should never resolve to the sign-in/selector/exchange/logout entry
 * points. Compared on pathname only (trailing slash normalized) so real deep
 * links like `/company/acme/app/123/...` are unaffected.
 */
const AUTH_LANDING_PATHS = new Set<string>([
  LogInPaths.company, // /company (and /company/)
  LogInPaths.selector, // /company/selector
  LogInPaths.signIn, // /company/sign-in
  LogInPaths.cbidpExchange, // /cbidp-exchange
  LogInPaths.logout, // /logout
]);

const normalizePath = (pathname: string): string =>
  pathname.length > 1 && pathname.endsWith('/')
    ? pathname.slice(0, -1)
    : pathname;

/**
 * True when a redirect target would leave the current origin — either an
 * absolute URL to a different origin, or a `//host` / `/\host` path that
 * `window.location.assign` treats as protocol-relative (and unparseable input,
 * which we treat as unsafe). This is the subset of `toSafeInternalRedirect`'s
 * rejections that represents a genuine host mismatch, as opposed to merely
 * declining a same-origin auth-landing path. `cbidp-exchange` uses it so its
 * "Redirect Blocked for Security" screen fires only for real cross-origin
 * attempts, not for e.g. a bare `/company/sign-in` from a plain sign-in.
 */
export const isOffOriginRedirect = (
  target: string | undefined | null
): boolean => {
  if (!target) {
    return false;
  }
  try {
    const url = new URL(target, window.location.origin);
    if (url.origin !== window.location.origin) {
      return true;
    }
    return /^\/[/\\]/.test(url.pathname + url.search + url.hash);
  } catch {
    return true;
  }
};

/**
 * Resolves a redirect target to a same-origin relative path. Anything that
 * points off-origin (or cannot be parsed) collapses to the fallback so a
 * query-param-supplied `redirectUri` cannot be used for an open redirect.
 * Mirrors the same-origin validation used in `cbidp-exchange`.
 */
export const toSafeInternalRedirect = (
  target: string | undefined | null,
  fallback: string = LogInPaths.company
): string => {
  if (!target) {
    return fallback;
  }
  // A "//host" / "/\\host" path passes the origin check but assign would treat
  // it as off-origin; an absolute off-origin URL is rejected too.
  if (isOffOriginRedirect(target)) {
    return fallback;
  }
  try {
    const url = new URL(target, window.location.origin);
    const relativePath = url.pathname + url.search + url.hash;
    if (AUTH_LANDING_PATHS.has(normalizePath(url.pathname))) {
      // Exception: `/company/sign-in?redirectUri=<deep link>` is the documented
      // sign-in pass-through wrapper the CBIDP flow round-trips through the IdP
      // and hands to `cbidp-exchange`. It must survive so the exchange page can
      // forward to the real target (which the sign-in landing then re-validates
      // via this same helper). A bare `/company/sign-in`, or any other
      // auth-landing target, still collapses to the fallback, so a
      // self-referential redirectUri cannot loop.
      const isSignInPassThrough =
        normalizePath(url.pathname) === LogInPaths.signIn &&
        !!url.searchParams.get('redirectUri');
      if (!isSignInPassThrough) {
        return fallback;
      }
    }
    return relativePath;
  } catch {
    return fallback;
  }
};
